'use client';

import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo } from 'react';
import {
  PaginationEllipsis,
  PaginationLink,
  PaginationNavLink,
  Pagination as StyledPagination,
} from '@//components/Pagination';
import type { ElasticSearchResponseDocumentListPagination } from '@//ElasticSearch';
import { createPaginationWithEllipsis } from '@//utils/pagination';
import { sanitizeParams } from '@//utils/vehicles-search/utils';

const resolveHref = (rootUrl: string, q?: string) => {
  const params = sanitizeParams(new URLSearchParams(q), { keepOffset: true });
  params.delete('branches'); // Remove branch filter for retailer pagination
  const hasOffset = params.has('offset');
  const hasQ = params.has('q');
  if (hasOffset || hasQ) {
    return `${rootUrl}${params.toString() ? `?${params}` : ''}`;
  }

  const paramsAsString = params.toString();
  const href = paramsAsString ? `?${paramsAsString}` : window.location.pathname;

  return href;
};

interface RetailerVehiclesPaginationProps {
  rootUrl: string;
  pagination?: ElasticSearchResponseDocumentListPagination;
}

const RetailerVehiclesPagination = ({ rootUrl, pagination }: RetailerVehiclesPaginationProps) => {
  const t = useTranslations('other.pagination');
  const router = useRouter();

  const onClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, queryString: string) => {
      e.preventDefault();
      e.stopPropagation();
      const nextParams = sanitizeParams(queryString, { keepOffset: true });
      nextParams.delete('branches'); // Remove branch filter for retailer pagination
      const nextParamsAsString = nextParams.toString();
      const nextUrl = `${rootUrl}${nextParamsAsString ? `?${nextParamsAsString}` : ''}`;
      router.push(nextUrl, { scroll: true });

      // Focus the vehicles grid after navigation
      setTimeout(() => {
        const vehiclesElement = document.getElementById('retailer-vehicles');
        if (vehiclesElement) {
          vehiclesElement.focus();
          vehiclesElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    },
    [router],
  );

  const paginationItems = useMemo(
    () => createPaginationWithEllipsis(pagination?.pages || []),
    [pagination?.pages],
  );

  if (!pagination) {
    return null;
  }

  const prevQuery = pagination.previousPage?.query;
  const nextQuery = pagination.nextPage?.query;

  return (
    <StyledPagination testId="retailer-pagination" ariaLabel={t('ariaLabel')} center>
      <PaginationNavLink
        variant="prev"
        href={prevQuery ? resolveHref(prevQuery) : undefined}
        testId="retailer-pagination-prev"
        onClick={prevQuery ? (e) => onClick(e, prevQuery) : undefined}
      />
      {paginationItems.map((item, index) => {
        if (item.isEllipsis) {
          return (
            <PaginationEllipsis
              key={`ellipsis-${index}`}
              testId={`retailer-pagination-ellipsis-${index}`}
            />
          );
        }

        return (
          <PaginationLink
            key={item.displayName}
            href={resolveHref(rootUrl, item.query!)}
            onClick={(e) => onClick(e, item.query!)}
            title={t('goTo.title', { page: item.displayName })}
            testId={`retailer-pagination-page-${item.displayName}`}
            isActive={item.selected}
          >
            {item.displayName}
          </PaginationLink>
        );
      })}
      <PaginationNavLink
        variant="next"
        href={nextQuery ? resolveHref(rootUrl, nextQuery) : undefined}
        testId="retailer-pagination-next"
        onClick={nextQuery ? (e) => onClick(e, nextQuery) : undefined}
      />
    </StyledPagination>
  );
};

export default RetailerVehiclesPagination;
